import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { NgForm } from '@angular/forms';
import { ApiError, AuthService, BaseFormService, ConfigService, FormsUtilsService, MessageService } from '../../../core';
import { ConfigEmpresa } from '../../configuracoes/app/empresa';
import { Pessoa } from '../../pessoa';
import { TypeaheadClientePessoaComponent } from '../../pessoa/cliente/typeahead';
import { Representante } from '../../pessoa/representante/representante';
import { RepresentanteCrudHttpService } from '../../pessoa/representante/services';
import { TituloReceberAuthGuardService, TituloReceberCrudHttpService } from '../services';
import { TituloReceber } from '../titulo-receber';
import { NotaCardsComponent, NotaFilter, NotaFormInsertComponent } from '../../nota';
import { ClienteSearchesHttpService } from '../../pessoa/cliente/services';
import { take } from 'rxjs/operators';

interface Config {
    canInsert: boolean;
    canEdit: boolean;
    isShowNotas: boolean;
}

@Component({
    selector: 'sgt-titulo-receber-form',
    templateUrl: './titulo-receber-form.component.html',
    styleUrls: ['./titulo-receber-form.component.less']
})
export class TituloReceberFormComponent implements OnInit {
    config: Config;
    configEmpresa: ConfigEmpresa;
    isRepresentante: boolean;

    entity: TituloReceber;
    isLoading: boolean;
    isLoadingFinished: boolean;
    editMode: boolean;
    readonly: boolean;

    @ViewChild('form') form: NgForm;
    @ViewChild('formElement') formElement: ElementRef;

    /** Config */
    @Input() id: number = null; // ID fixo do título que deve ser carregado
    // Visualização
    @Input() embedded = false; // Se o component é embutido (Útil quando o compoment for utilizado dentro de outro ao invés de ser usado no route)
    @Input() showCliente = true; // Se deve exibir o cliente
    @Input() showRepresentante = true; // Se deve exibir o representante

    @Output() afterCancel = new EventEmitter<any>();

    // Cliente
    messageCompletarCadastroCliente = 'É necessário completar o cadastro do cliente para poder salvar o título a receber';
    @ViewChild('typeaheadCliente') typeaheadCliente: TypeaheadClientePessoaComponent;

    // Notas
    @ViewChild('notaCards') notaCards: NotaCardsComponent;
    @ViewChild('notaFormInsert') notaFormInsert: NotaFormInsertComponent;
    @ViewChild('notasContent') notasContent: ElementRef;

    constructor(private activatedRoute: ActivatedRoute,
                private authService: AuthService,
                private messageService: MessageService,
                private baseFormService: BaseFormService,
                private configService: ConfigService,
                private formsUtilsService: FormsUtilsService,
                private clienteSearchesHttpService: ClienteSearchesHttpService,
                private representanteCrudHttpService: RepresentanteCrudHttpService,
                private tituloReceberAuthGuardService: TituloReceberAuthGuardService,
                private tituloReceberCrudHttpService: TituloReceberCrudHttpService) { }

    ngOnInit() {
        this.configEmpresa = this.configService.getEmpresa();
        this.isRepresentante = this.authService.isRepresentante();

        if (!this.embedded) {
            if (this.id && Number(this.id) > 0) { // Pesquisa pelo ID fixo informado
                this.load(this.id);
            } else { // Pesquisa pelo ID informado no route
                this.baseFormService.getIdParam(this.activatedRoute).then(id => {
                    if (!id) {
                        this.insert();
                    } else {
                        this.edit(id);
                    }
                });
            }
        }
    }

    private setConfig() {
        if (!this.entity) {
            return;
        }

        this.config = {
            canInsert: !this.readonly && this.tituloReceberAuthGuardService.canInsert(),
            canEdit: !this.readonly && this.tituloReceberAuthGuardService.canEdit(this.entity.id),
            isShowNotas: this.entity.id > 0
        };
    }


    load(id: number) {
        this.isLoading = true;
        this.isLoadingFinished = false;
        this.tituloReceberCrudHttpService.load(id)
            .subscribe(
                entity => {
                    this.entity = entity;
                    this.isLoading = false;
                    this.isLoadingFinished = true;
                    this.afterLoad();
                },
                (error: ApiError) => {
                    this.isLoading = false;
                    this.isLoadingFinished = true;
                    this.messageService.error(error.message, 'Erro ao carregar o título a rececer', error);
                    this.cancel();
                }
            );
    }

    /** Após carrega */
    private async afterLoad() {
        this.setConfig();

        // Verificando se deve desabilitar os campos quando título a receber não poder ser editado
        setTimeout(() => {
            if (!this.config.canEdit || this.readonly) {
                this.formsUtilsService.disableAllFields(this.formElement);
            }

            // Pesquisando as notas do título
            this.searchNotas();
        }, 100);

        // Obtendo o ID do cliente da pessoa do pedido
        this.entity.idCliente = await this.getIdCliente();
    }

    /** Insere um novo título a receber */
    insert() {
        this.editMode = false;
        this.readonly = false;
        this.entity = new TituloReceber(this.authService.getIdEmpresa());

        this.setConfig();
    }

    /** Edita um título a receber */
    edit(id: number) {
        this.editMode = true;
        this.readonly = false;
        this.load(id);
    }

    /** Edita um título a receber */
    visualizar(id: number) {
        this.editMode = false;
        this.readonly = true;
        this.load(id);
    }

    cancel() {
        // Somente quando não for o form embedded
        if (!this.embedded) {
            this.baseFormService.cancel(this.activatedRoute);
        }

        this.afterCancel.emit();
    }

    /** Obtem o representante do cliente pelo id da pessoa informado */
    private getRepresentanteDoCliente(idRepresentante: number) {
        if (!idRepresentante) {
            return;
        }

        this.representanteCrudHttpService.find(idRepresentante)
            .subscribe(
                representante => {
                    this.entity.idRepresentante = representante.id;
                    this.entity.representante = representante;
                },
                (error: ApiError) => this.messageService.error(error.message, 'Erro ao pesquisar o representante vinculado ao cliente selecionado', error)
            );
    }

    /** Obtem o ID do cliente através da pessoa do pedido */
    private getIdCliente(): Promise<number> {
        return new Promise<number>((resolve) => {
            if (!this.entity || !this.entity.idPessoa) {
                resolve(null);
                return;
            }

            this.clienteSearchesHttpService.getIdByPessoa(this.entity.idPessoa)
                .pipe(
                    take(1)
                )
                .subscribe(
                    idCliente => resolve(idCliente),
                    () => resolve(null)
                );
        });
    }

    /** Ao alterar o cliente/pessoa no typeahead */
    onSelectCliente(pessoa: Pessoa) {
        if (pessoa) {
            this.entity.idPessoa = pessoa.id;
            this.entity.idCliente = pessoa.idCliente;

            // Se não for um representante e o cliente possuir representante, então tentamos obter o representante do cliente selecionado
            if (!this.isRepresentante && pessoa.idRepresentante) {
                this.getRepresentanteDoCliente(pessoa.idRepresentante);
            }
        } else {
            this.clearCliente();
        }
    }

    /** Remove o cliente/pessoa */
    clearCliente() {
        this.entity.idPessoa = null;
        this.entity.pessoa = null;
        this.entity.idCliente = null;
    }

    /** Ao selecionar o representante */
    onSelectRepresentante(representante: Representante) {
        if (representante) {
            this.entity.idRepresentante = representante.id;
        } else {
            this.clearRepresentante();
        }
    }

    /** Remove o representante */
    clearRepresentante() {
        this.entity.idRepresentante = null;
        this.entity.representante = null;
    }

    /** Valida o save do título a receber */
    private isValid(): boolean {
        // Form inválido
        this.formsUtilsService.makeAllFieldsDirty(this.form);
        if (!this.form.valid) {
            this.formsUtilsService.showMsgFormInvalid();
            return false;
        }

        return true;
    }

    private async searchNotas() {
        try {
            const filter = new NotaFilter();
            // Cliente
            // if (this.entity.idCliente) {
            //     filter.idsReferenciaClientes = [this.entity.idCliente];
            // }
            // Título a Receber
            filter.idsReferenciaTitulosReceber = [this.entity.id];

            await this.notaCards.search(filter);
        } catch (error) {
            this.messageService.error(error.message, 'Erro ao pesquisar as notas do título a receber', error);
        }
    }

    afterInsertNota() {
        jQuery(this.notasContent.nativeElement).animate({scrollTop: 0});
    }


    /** Antes de salvar o título a receber */
    private beforeSave() {
        // Pagamentos
        // ...

        // Notas
        if (this.notaFormInsert?.isInsertingValid()) {
            this.notaCards?.insert(this.notaFormInsert.nota);
        }
    }

    /** Salva o título a receber */
    save(): Promise<TituloReceber> {
        return new Promise<TituloReceber>((resolve, reject) => {
            if (!this.isValid()) {
                reject();
                return false;
            }

            this.beforeSave();
            this.baseFormService.save<TituloReceber>(this.entity, this.form, this.editMode, this.tituloReceberCrudHttpService)
                .then(entity => {
                    this.entity = entity;
                    this.messageService.success('', `Título a receber ${this.entity.numeroErp} salvo com sucesso`);
                    this.afterSave();
                    resolve(this.entity);
                })
                .catch(error => reject(error));
        });
    }

    private afterSave() { }

    /** Ao clicar no botão de salvar */
    async saveClick() {
        try {
            await this.save();
            this.cancel();
        } catch (error) {
            //
        }
    }

    clear() {
        this.entity = null;
    }
}
